import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MdSearch,
  MdFavoriteBorder,
  MdList,
  MdEvent,
  MdFavorite,
  MdChevronRight,
  MdAlarm,
  MdAlarmOn,
  MdLock,
  MdLockOpen,
  MdPictureInPictureAlt,
  MdBlock,
} from 'react-icons/md';
import xtreamAPI, { XtreamContent, UICategory, XtreamCurrentLiveProgram } from '../../api/xtreamAPI';
import { getCachedLiveStreams } from '../../utils/liveStreamsCache';
import { ensureHostCache, getCachedEpg, setCachedEpg, loadAllCachedEpg } from '../../api/epgCache';
import { getXtreamServerUrl, useAuth } from '../../contexts/AuthContext';
import { useDevice } from '../../contexts/DeviceContext';
import { DEMO_CHANNELS, DEMO_CATEGORY } from '../../data/demoChannels';
import { useFavorites } from '../../hooks/useFavorites';
import { useSchedules } from '../../hooks/useSchedules';
import { useParentalControls } from '../../contexts/ParentalControlsContext';
import { isKidsCategory } from '../../utils/kidsCategories';
import { MiniPlayer } from '../../components/MiniPlayer/MiniPlayer';
import type { MiniPlayerHandle, AudioTrackInfo, SubtitleTrackInfo, LiveTimeshiftInfo, PlaybackEngine } from '../../components/MiniPlayer/MiniPlayer';
import { PlaybackStatusOverlay } from '../../components/PlaybackStatusOverlay/PlaybackStatusOverlay';
import { isAuroraDesktop } from '../../utils/desktopRuntime';
import { getMediaKeyAction } from '../../utils/tizenKeys';
import PasswordModal, { PasswordModalMode } from '../../components/PasswordModal/PasswordModal';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';
import { useParentalContentLocks } from '../../hooks/useParentalContentLocks';
import './LiveTVCategories.css';

type FixedCategoryId = 'search' | 'schedule' | 'favorites' | 'blockCategories' | 'all';

type CategoryNode =
  | { id: FixedCategoryId; kind: 'fixed'; label: string }
  | { id: string; kind: 'dynamic'; label: string; categoryId: string };

type FocusArea = 'categories' | 'channels' | 'favorite' | 'epg-days' | 'epg-list' | 'epg-schedule';

type PendingParentalAction =
  | { type: 'editCategories' }
  | { type: 'toggleChannel'; streamId: string; blocked: boolean }
  | { type: 'playChannel'; streamId: string; fullscreen: boolean };

interface LiveTVEntryState {
  selectedChannel: XtreamContent | null;
  channels: XtreamContent[];
  categories: UICategory[];
  fullscreenRequest?: number;
}

interface LiveTVCategoriesProps {
  initialState: LiveTVEntryState | null;
  onBack: () => void;
  isActive?: boolean;
  playbackActive?: boolean;
  homePreview?: boolean;
  onPlayingChannelChange?: (entryState: LiveTVEntryState) => void;
  onNavigate?: (sectionId: string) => void;
}

const isBackKey = (event: KeyboardEvent): boolean => {
  var k = event.key || '';
  var code = (event as any).keyCode || (event as any).which || 0;
  // Tizen 3 / Samsung remote: keyCode 10009
  // LG webOS: keyCode 461
  // Standard browsers: Escape
  // BrowserBack / Back / GoBack: older Chrome / TV browsers
  if (k === 'Escape' || k === 'Esc') return true;
  if (k === 'Back' || k === 'BrowserBack' || k === 'GoBack') return true;
  if (code === 10009 || code === 461) return true;
  return false;
};

/** Fixed height of each channel row (logo 52px + padding 8+8). Must match CSS. */
const CHANNEL_ITEM_H = 68;
/** Extra rows to render above and below the visible window. */
const CHANNEL_BUFFER = 6;

const formatDate = (date: Date) => {
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}`;
};

const WEEKDAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

const toDayKey = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const toProgramDayKey = (timestamp: number) => toDayKey(new Date(timestamp));

const formatHHMM = (timestamp: number) => {
  const date = new Date(timestamp);
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
};

const formatOffset = (seconds: number) => {
  const whole = Math.max(0, Math.round(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
};

const ScrollingChannelName: React.FC<{ name: string; focused: boolean }> = ({ name, focused }) => {
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    element.scrollLeft = 0;
    if (!focused) return;

    let pause = 25;
    const timer = window.setInterval(() => {
      const end = element.scrollWidth - element.clientWidth;
      if (end <= 0 || pause-- > 0) return;
      if (element.scrollLeft >= end - 1) {
        element.scrollLeft = 0;
        pause = 25;
      } else {
        element.scrollLeft += 1;
      }
    }, 40);

    return () => {
      window.clearInterval(timer);
      element.scrollLeft = 0;
    };
  }, [focused, name]);

  return (
    <span ref={ref} className={`livetvcat__channel-name${focused ? ' is-scrolling' : ''}`}>{name}</span>
  );
};

export const LiveTVCategories: React.FC<LiveTVCategoriesProps> = ({ initialState, onBack, isActive = true, playbackActive = isActive, homePreview = false, onPlayingChannelChange, onNavigate }) => {
  const { authData } = useAuth();
  const { t } = useTranslation();
  const { isDemo } = useDevice();
  const {
    isBlocked,
    isKidsMode,
    hasPassword,
    verifyPassword,
    setPassword,
  } = useParentalControls();
  const {
    isEnabled: isParentalEnabled,
    isCategoryBlocked,
    isLiveChannelBlocked,
    isContentRestricted,
    setBlockedCategories,
    setLiveChannelBlocked,
  } = useParentalContentLocks();
  const { getFavoritesByType, isFavorite, toggleFavorite } = useFavorites();
  const { schedules, toggleSchedule, isScheduled } = useSchedules();

  const [channels, setChannels] = useState<XtreamContent[]>(initialState?.channels || []);
  const [categories, setCategories] = useState<UICategory[]>(initialState?.categories || []);
  const [loading, setLoading] = useState<boolean>(!initialState?.channels?.length);
  const [focusArea, setFocusArea] = useState<FocusArea>('channels');
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(0);
  const [committedCategoryIndex, setCommittedCategoryIndex] = useState(0);
  const categoryDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [focusedChannelIndex, setFocusedChannelIndex] = useState(0);
  const [playingStreamId, setPlayingStreamId] = useState<string>(
    String(initialState?.selectedChannel?.stream_id || '')
  );
  const [selectedDayIndex, setSelectedDayIndex] = useState(3);
  const [selectedProgramIndex, setSelectedProgramIndex] = useState(0);
  const [epgPanelOpen, setEpgPanelOpen] = useState(false);
  const [columnsHidden, setColumnsHidden] = useState(false);
  const [fsoVisible, setFsoVisible] = useState(false);
  const [fsoFocusedBtn, setFsoFocusedBtn] = useState<'pip' | 'lock' | null>(null);
  const desktopPictureInPicture = isAuroraDesktop();
  const [categoryBlockMode, setCategoryBlockMode] = useState(false);
  const [draftBlockedCategoryIds, setDraftBlockedCategoryIds] = useState<Set<string>>(new Set());
  const [showCategorySaveModal, setShowCategorySaveModal] = useState(false);
  const [parentalPasswordMode, setParentalPasswordMode] = useState<PasswordModalMode | null>(null);
  const [parentalPasswordError, setParentalPasswordError] = useState('');
  const [pendingParentalAction, setPendingParentalAction] = useState<PendingParentalAction | null>(null);
  const [temporarilyUnlockedStreamId, setTemporarilyUnlockedStreamId] = useState('');
  const [videoResolution, setVideoResolution] = useState<{ width: number; height: number } | null>(null);
  const [epgByStreamId, setEpgByStreamId] = useState<Record<string, XtreamCurrentLiveProgram[]>>({});
  const [throughputMbps, setThroughputMbps] = useState<number | null>(null);
  const [clockNow, setClockNow] = useState(Date.now());
  const [playbackEngine, setPlaybackEngine] = useState<PlaybackEngine | null>(null);
  const [timeshiftInfo, setTimeshiftInfo] = useState<LiveTimeshiftInfo | null>(null);
  const [audioTracks, setAudioTracks] = useState<AudioTrackInfo[]>([]);
  const [activeAudioId, setActiveAudioId] = useState<number>(0);
  const [subtitleTracks, setSubtitleTracks] = useState<SubtitleTrackInfo[]>([]);
  const [activeSubtitleId, setActiveSubtitleId] = useState<number>(-1);
  const [trackDialog, setTrackDialog] = useState<'audio' | 'subtitle' | null>(null);
  const [trackDialogIndex, setTrackDialogIndex] = useState(0);
  const [trackSwitchMessage, setTrackSwitchMessage] = useState<string | null>(null);
  const trackSwitchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [mediaKeyMessage, setMediaKeyMessage] = useState<string | null>(null);
  const mediaKeyMessageTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [channelScrollTop, setChannelScrollTop] = useState(0);

  const categoryItemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const channelListRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const epgDayRefs = useRef<Array<HTMLDivElement | null>>([]);
  const epgItemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const positionedChannelRequestRef = useRef('');
  // Set to true when the category is auto-positioned to match the initial channel.
  // Prevents the category-change reset effect from clearing the channel focus.
  const initialCategorySetRef = useRef(false);

  // EPG background-download queue — all refs to avoid triggering re-renders on queue mutations
  const mountedRef = useRef(true);
  const epgLoadedRef = useRef(new Set<string>());
  const epgQueuedRef = useRef(new Set<string>());
  const epgQueueRef = useRef<string[]>([]);
  const epgWorkerActiveRef = useRef(false);
  // Ordem de inserção para LRU do epgByStreamId (máx EPG_MEM_LIMIT entradas em memória)
  const epgInsertionOrderRef = useRef<string[]>([]);
  const EPG_MEM_LIMIT = 300;
  const availableDaysLengthRef = useRef(0);
  const fsoHideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const miniPlayerRef = useRef<MiniPlayerHandle>(null);
  const prevInitialStreamIdRef = useRef<string>('');
  const pendingInitialStreamIdRef = useRef<string>('');
  // Flag para suprimir onScroll quando o scroll é programático (evita double re-render)
  const isProgrammaticScrollRef = useRef(false);
  const promptedBlockedStreamRef = useRef('');
  const handledFullscreenRequestRef = useRef(0);

  const isCategoryMarked = useCallback(
    (id: string) => isCategoryBlocked('live', id),
    [isCategoryBlocked]
  );
  const isChannelMarked = useCallback(
    (id: string) => isLiveChannelBlocked(id),
    [isLiveChannelBlocked]
  );
  const isChannelRestricted = useCallback(
    (channel: XtreamContent) => isContentRestricted(
      'live',
      String(channel.stream_id || ''),
      String(channel.category_id || '')
    ),
    [isContentRestricted]
  );

  // A Home pode terminar de carregar as listas depois que a instância
  // persistente da Live TV já foi montada.
  useEffect(() => {
    if (channels.length || !initialState?.channels?.length) return;
    setChannels(initialState.channels);
    setCategories(initialState.categories || []);
    setLoading(false);
  }, [channels.length, initialState]);

  // Debounce de 300ms antes de commitar a categoria selecionada
  // (evita flood de renders/EPG ao navegar rápido com o controle remoto em qualquer dispositivo)
  useEffect(() => {
    if (categoryDebounceRef.current) clearTimeout(categoryDebounceRef.current);
    categoryDebounceRef.current = setTimeout(() => {
      setCommittedCategoryIndex(selectedCategoryIndex);
    }, 300);
    return () => {
      if (categoryDebounceRef.current) clearTimeout(categoryDebounceRef.current);
    };
  }, [selectedCategoryIndex]);

  // ── Demo mode: bypass Xtream entirely and show built-in demo channels ─────
  useEffect(() => {
    if (!isDemo) return;
    setChannels(DEMO_CHANNELS);
    setCategories([DEMO_CATEGORY]);
    setLoading(false);
    setPlayingStreamId((prev) => prev || String(DEMO_CHANNELS[0].stream_id));
  }, [isDemo]);

  useEffect(() => {
    // In demo mode channel data comes from the effect above.
    if (isDemo) return;
    if (!authData) return;

    const { server, user } = authData;
    const host = getXtreamServerUrl(server);
    xtreamAPI.setCredentials(host, user.username, user.password);

    // Carrega cache do IndexedDB (limpa se host mudou, pré-popula epgByStreamId)
    void ensureHostCache(host).then(async () => {
      const cached = await loadAllCachedEpg();
      const ids = Object.keys(cached);
      if (ids.length > 0 && mountedRef.current) {
        setEpgByStreamId(cached);
        ids.forEach(id => epgLoadedRef.current.add(id));
      }
    });

    if (initialState?.channels?.length) return;

    let cancelled = false;
    setLoading(true);

    const load = async () => {
      try {
        const liveStreams = await getCachedLiveStreams();
        if (cancelled) return;
        const liveCategories = await xtreamAPI.getLiveCategoriesForUI();
        if (cancelled) return;

        setChannels(liveStreams);
        setCategories(liveCategories);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [authData, isDemo, initialState?.channels?.length]);

  const categoryNodes = useMemo<CategoryNode[]>(() => {
    const visibleCategories = isKidsMode
      ? categories.filter(c => isKidsCategory(c.name))
      : categories;

    const dynamic = visibleCategories
      .filter((category) => categoryBlockMode || !isParentalEnabled || !isCategoryMarked(String(category.id || '')))
      .map((category) => ({
        id: `category-${category.id}`,
        kind: 'dynamic' as const,
        label: category.name,
        categoryId: String(category.id || ''),
      }));

    if (categoryBlockMode) return dynamic;

    const fixed: CategoryNode[] = [
      { id: 'search', kind: 'fixed', label: t('liveTV.search') },
      { id: 'schedule', kind: 'fixed', label: t('liveTV.schedules') },
      { id: 'favorites', kind: 'fixed', label: t('common.favorites') },
      { id: 'blockCategories', kind: 'fixed', label: t('liveTV.blockCategories') },
      { id: 'all', kind: 'fixed', label: t('liveTV.allChannels') },
    ];

    return [...fixed, ...dynamic];
  }, [categories, categoryBlockMode, isCategoryMarked, isKidsMode, isParentalEnabled, t]);

  useEffect(() => {
    setSelectedCategoryIndex((index) => Math.min(index, Math.max(0, categoryNodes.length - 1)));
    setCommittedCategoryIndex((index) => Math.min(index, Math.max(0, categoryNodes.length - 1)));
  }, [categoryNodes.length]);

  useEffect(() => {
    if (!categoryNodes.length || !initialState?.selectedChannel) return;
    const targetCategoryId = String(initialState.selectedChannel.category_id || '');
    if (!targetCategoryId) return;

    const dynamicIndex = categoryNodes.findIndex(
      (node) => node.kind === 'dynamic' && node.categoryId === targetCategoryId
    );

    // Mark that this category change is from initial positioning so the reset
    // effect knows not to clear the channel focus.
    initialCategorySetRef.current = true;
    if (dynamicIndex >= 0) {
      setSelectedCategoryIndex(dynamicIndex);
    } else {
      const allIndex = categoryNodes.findIndex((node) => node.kind === 'fixed' && node.id === 'all');
      setSelectedCategoryIndex(allIndex >= 0 ? allIndex : 0);
    }
  }, [categoryNodes, initialState?.fullscreenRequest, initialState?.selectedChannel]);

  // selectedCategoryNode: para highlight visual (reage imediatamente ao índice visual)
  const selectedCategoryNode = categoryNodes[selectedCategoryIndex] || categoryNodes[0] || null;
  // committedCategoryNode: para filtrar canais e disparar EPG (debounced no Android WebView)
  const committedCategoryNode = categoryNodes[committedCategoryIndex] || categoryNodes[0] || null;

  const favoriteIds = useMemo(
    () => new Set(getFavoritesByType('live').map((item) => item.id)),
    [getFavoritesByType]
  );

  const scheduledStreamIdSet = useMemo(
    () => new Set(schedules.map((s) => s.streamId)),
    [schedules],
  );

  const filteredChannels = useMemo(() => {
    // In kids mode, only kids-category channels are accessible
    const kidsChannelIds = isKidsMode
      ? new Set(categories.filter(c => isKidsCategory(c.name)).map(c => String(c.id)))
      : null;
    const kidsFilteredChannels = kidsChannelIds
      ? channels.filter(ch => kidsChannelIds.has(String(ch.category_id || '')))
      : channels;
    const baseChannels = isParentalEnabled
      ? kidsFilteredChannels.filter((channel) => !isChannelRestricted(channel))
      : kidsFilteredChannels;

    if (!committedCategoryNode) return baseChannels;

    if (committedCategoryNode.kind === 'fixed') {
      if (committedCategoryNode.id === 'favorites') {
        return baseChannels.filter((channel) => favoriteIds.has(String(channel.stream_id || '')));
      }
      if (committedCategoryNode.id === 'schedule') {
        return baseChannels.filter((channel) => scheduledStreamIdSet.has(String(channel.stream_id || '')));
      }
      if (committedCategoryNode.id === 'all' || committedCategoryNode.id === 'blockCategories') {
        return baseChannels;
      }
      if (committedCategoryNode.id === 'search') {
        const q = searchQuery.trim().toLowerCase();
        return q ? baseChannels.filter(ch => (ch.name || '').toLowerCase().includes(q)) : baseChannels;
      }
      return baseChannels;
    }

    return baseChannels.filter(
      (channel) => String(channel.category_id || '') === String(committedCategoryNode.categoryId || '')
    );
  }, [channels, categories, isKidsMode, isParentalEnabled, isChannelRestricted, favoriteIds, committedCategoryNode, scheduledStreamIdSet, searchQuery]);

  useEffect(() => {
    if (!isActive) {
      positionedChannelRequestRef.current = '';
      return;
    }

    if (!filteredChannels.length) {
      setFocusedChannelIndex(0);
      return;
    }

    const initialChannelId = String(initialState?.selectedChannel?.stream_id || '');
    const positionRequest = `${initialChannelId}:${initialState?.fullscreenRequest || 0}`;

    // Position once per entry request; category switches after that start at the top.
    if (positionedChannelRequestRef.current !== positionRequest && initialChannelId) {
      const targetIndex = filteredChannels.findIndex(
        (channel) => String(channel.stream_id || '') === initialChannelId
      );
      if (targetIndex >= 0) {
        positionedChannelRequestRef.current = positionRequest;
        setFocusArea('channels');
        setFocusedChannelIndex(targetIndex);
        return;
      }
    }

    // Clamp to valid range (handles list shrinking on category/search switch)
    setFocusedChannelIndex((prev) => Math.min(prev, filteredChannels.length - 1));
  }, [filteredChannels, initialState?.fullscreenRequest, initialState?.selectedChannel?.stream_id, isActive]);

  const focusedChannel = filteredChannels[focusedChannelIndex] || null;
  const focusedStreamId = String(focusedChannel?.stream_id || '');
  const playingChannel =
    channels.find((channel) => String(channel.stream_id || '') === String(playingStreamId || '')) ||
    focusedChannel ||
    null;
  const playingChannelIsRestricted = Boolean(
    playingChannel &&
    isChannelRestricted(playingChannel) &&
    String(playingChannel.stream_id || '') !== temporarilyUnlockedStreamId
  );
  const playbackChannel = playingChannelIsRestricted ? null : playingChannel;
  const channelLocked = Boolean(playingStreamId && isChannelMarked(playingStreamId));

  const runParentalAction = useCallback((action: PendingParentalAction) => {
    if (action.type === 'editCategories') {
      setDraftBlockedCategoryIds(new Set(
        categories
          .map((category) => String(category.id || ''))
          .filter(isCategoryMarked)
      ));
      setCategoryBlockMode(true);
      setSelectedCategoryIndex(0);
      setCommittedCategoryIndex(0);
      setFocusArea('categories');
      return;
    }

    if (action.type === 'toggleChannel') {
      setLiveChannelBlocked(action.streamId, !action.blocked);
      if (!action.blocked && isParentalEnabled) {
        setTemporarilyUnlockedStreamId(playingStreamId);
      }
      return;
    }

    setTemporarilyUnlockedStreamId(action.streamId);
    setPlayingStreamId(action.streamId);
    if (action.fullscreen) {
      setColumnsHidden(true);
      setFsoVisible(false);
      setFsoFocusedBtn(null);
    }
  }, [categories, isCategoryMarked, isParentalEnabled, playingStreamId, setLiveChannelBlocked]);

  const requestParentalAction = useCallback((action: PendingParentalAction) => {
    setPendingParentalAction(action);
    setParentalPasswordError('');
    setParentalPasswordMode(hasPassword() ? 'verify' : 'set');
  }, [hasPassword]);

  const handleParentalPasswordConfirm = useCallback((password: string) => {
    if (!pendingParentalAction) return;
    if (parentalPasswordMode === 'verify' && !verifyPassword(password)) {
      setParentalPasswordError(t('parental.wrongPassword'));
      return;
    }
    if (parentalPasswordMode === 'set') setPassword(password);
    runParentalAction(pendingParentalAction);
    setPendingParentalAction(null);
    setParentalPasswordMode(null);
  }, [parentalPasswordMode, pendingParentalAction, runParentalAction, setPassword, t, verifyPassword]);

  const requestChannelPlayback = useCallback((channel: XtreamContent, fullscreen: boolean) => {
    const streamId = String(channel.stream_id || '');
    if (!streamId) return;
    if (isChannelRestricted(channel) && streamId !== temporarilyUnlockedStreamId) {
      requestParentalAction({ type: 'playChannel', streamId, fullscreen });
      return;
    }
    setTemporarilyUnlockedStreamId((current) => current === streamId ? current : '');
    setPlayingStreamId(streamId);
    if (fullscreen) {
      setColumnsHidden(true);
      setFsoVisible(false);
      setFsoFocusedBtn(null);
      if (fsoHideTimerRef.current) clearTimeout(fsoHideTimerRef.current);
    }
  }, [isChannelRestricted, requestParentalAction, temporarilyUnlockedStreamId]);

  const requestChannelLockToggle = useCallback(() => {
    if (!playingStreamId) return;
    requestParentalAction({
      type: 'toggleChannel',
      streamId: playingStreamId,
      blocked: channelLocked,
    });
  }, [channelLocked, playingStreamId, requestParentalAction]);

  const toggleDraftCategory = useCallback((categoryId: string) => {
    setDraftBlockedCategoryIds((current) => {
      const next = new Set(current);
      if (next.has(categoryId)) next.delete(categoryId);
      else next.add(categoryId);
      return next;
    });
  }, []);

  const saveBlockedCategories = useCallback(() => {
    setBlockedCategories('live', Array.from(draftBlockedCategoryIds));
    setShowCategorySaveModal(false);
    setCategoryBlockMode(false);
    setSelectedCategoryIndex(0);
    setCommittedCategoryIndex(0);
  }, [draftBlockedCategoryIds, setBlockedCategories]);

  const cancelBlockedCategories = useCallback(() => {
    setShowCategorySaveModal(false);
    setCategoryBlockMode(false);
    setSelectedCategoryIndex(0);
    setCommittedCategoryIndex(0);
  }, []);

  // Home controla o canal enquanto o player está no modo mini. A referência
  // pendente impede o canal anterior de ser reportado de volta ao App durante
  // o render intermediário da troca.
  useEffect(() => {
    const newStreamId = String(initialState?.selectedChannel?.stream_id || '');
    if (!newStreamId || newStreamId === prevInitialStreamIdRef.current) return;
    prevInitialStreamIdRef.current = newStreamId;
    if (newStreamId !== playingStreamId) {
      pendingInitialStreamIdRef.current = newStreamId;
      setPlayingStreamId(newStreamId);
    } else {
      pendingInitialStreamIdRef.current = '';
    }
  }, [initialState?.selectedChannel?.stream_id, playingStreamId]);

  useEffect(() => {
    const request = initialState?.fullscreenRequest || 0;
    if (!isActive || !request || request === handledFullscreenRequestRef.current) return;
    handledFullscreenRequestRef.current = request;
    setColumnsHidden(true);
    setEpgPanelOpen(false);
    setFsoVisible(false);
    setFsoFocusedBtn(null);
    if (fsoHideTimerRef.current) clearTimeout(fsoHideTimerRef.current);
  }, [initialState?.fullscreenRequest, isActive]);

  useEffect(() => {
    if (!playbackChannel) return;
    const playingId = String(playbackChannel.stream_id || '');
    const pendingId = pendingInitialStreamIdRef.current;
    if (pendingId && playingId !== pendingId) return;
    if (pendingId === playingId) pendingInitialStreamIdRef.current = '';
    onPlayingChannelChange?.({ selectedChannel: playbackChannel, channels, categories });
  }, [categories, channels, onPlayingChannelChange, playbackChannel]);

  useEffect(() => {
    if (!isActive || !playingChannel || !playingChannelIsRestricted || parentalPasswordMode) return;
    const streamId = String(playingChannel.stream_id || '');
    if (!streamId || promptedBlockedStreamRef.current === streamId) return;
    promptedBlockedStreamRef.current = streamId;
    requestParentalAction({ type: 'playChannel', streamId, fullscreen: columnsHidden });
  }, [columnsHidden, isActive, parentalPasswordMode, playingChannel, playingChannelIsRestricted, requestParentalAction]);

  useEffect(() => {
    if (!playingStreamId || playingStreamId === temporarilyUnlockedStreamId) return;
    setTemporarilyUnlockedStreamId('');
  }, [playingStreamId, temporarilyUnlockedStreamId]);

  useEffect(() => {
    if (playingStreamId) return;
    if (!focusedStreamId) return;
    setPlayingStreamId(focusedStreamId);
  }, [focusedStreamId, playingStreamId]);

  // Unmount cleanup so background worker stops trying to setState after unmount
  useEffect(() => {
    mountedRef.current = true;
    const clockTimer = setInterval(() => setClockNow(Date.now()), 30_000);
    return () => {
      mountedRef.current = false;
      clearInterval(clockTimer);
      if (fsoHideTimerRef.current) clearTimeout(fsoHideTimerRef.current);
    };
  }, []);

  // Reset media track info when a new channel starts playing
  useEffect(() => {
    setAudioTracks([]);
    setActiveAudioId(0);
    setSubtitleTracks([]);
    setActiveSubtitleId(-1);
  }, [playingStreamId]);

  // Enqueue stream IDs for EPG download. priority=true moves them to the front.
  const enqueueForEpg = useCallback((streamIds: string[], priority: boolean) => {
    if (priority) {
      const eligible = streamIds.filter(id => id && !epgLoadedRef.current.has(id));
      epgQueueRef.current = epgQueueRef.current.filter(id => !eligible.includes(id));
      epgQueueRef.current = [...eligible, ...epgQueueRef.current];
      eligible.forEach(id => epgQueuedRef.current.add(id));
    } else {
      const fresh = streamIds.filter(
        id => id && !epgQueuedRef.current.has(id) && !epgLoadedRef.current.has(id),
      );
      fresh.forEach(id => epgQueuedRef.current.add(id));
      epgQueueRef.current = [...epgQueueRef.current, ...fresh];
    }
  }, []);

  // Background worker: drains the queue 3 at a time with full 7-day EPG (get_simple_epg)
  const processEpgQueue = useCallback(async () => {
    if (epgWorkerActiveRef.current) return;
    epgWorkerActiveRef.current = true;
    const CONCURRENT = 3;
    while (epgQueueRef.current.length > 0 && mountedRef.current) {
      const batch = epgQueueRef.current.splice(0, CONCURRENT);
      // Coleta todos os resultados do batch e faz UM único setState — evita N re-renders
      const batchResults: Record<string, XtreamCurrentLiveProgram[]> = {};
      await Promise.all(batch.map(async (streamId) => {
        try {
          const cached = await getCachedEpg(streamId);
          const programs = cached ?? await xtreamAPI.getLiveEpgFull(streamId);
          if (!cached) void setCachedEpg(streamId, programs);
          epgLoadedRef.current.add(streamId);
          batchResults[streamId] = programs;
        } catch {
          epgLoadedRef.current.add(streamId);
          batchResults[streamId] = [];
        }
      }));
      if (mountedRef.current && Object.keys(batchResults).length > 0) {
        setEpgByStreamId(prev => {
          // Atualiza ordem de inserção (move para o fim se já existe)
          const order = epgInsertionOrderRef.current;
          Object.keys(batchResults).forEach(id => {
            const idx = order.indexOf(id);
            if (idx >= 0) order.splice(idx, 1);
            order.push(id);
          });
          // Aplica LRU: descarta entradas mais antigas se ultrapassar o limite
          const next = { ...prev, ...batchResults };
          if (order.length > EPG_MEM_LIMIT) {
            const toEvict = order.splice(0, order.length - EPG_MEM_LIMIT);
            toEvict.forEach(id => delete next[id]);
          }
          return next;
        });
      }
    }
    epgWorkerActiveRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Trigger EPG fetch for the visible window based on scroll position (virtual scroll).
  useEffect(() => {
    if (!filteredChannels.length) return;
    const containerH = channelListRef.current?.clientHeight ?? 580;
    // No Android WebView, deriva o scrollTop do índice focado — evita depender do estado
    // channelScrollTop que só atualiza com scroll real (não programático)
    // Deriva o scrollTop do índice focado — não depende do estado channelScrollTop
    // (que não atualiza para scroll programático, apenas para scroll real do usuário)
    const effectiveScrollTop = Math.max(0, focusedChannelIndex * CHANNEL_ITEM_H - containerH / 2);
    const startIdx = Math.max(0, Math.floor(effectiveScrollTop / CHANNEL_ITEM_H) - CHANNEL_BUFFER);
    const endIdx = Math.min(
      filteredChannels.length - 1,
      Math.ceil((effectiveScrollTop + containerH) / CHANNEL_ITEM_H) + CHANNEL_BUFFER,
    );
    const ids = filteredChannels
      .slice(startIdx, endIdx + 1)
      .map(ch => String(ch.stream_id || ''))
      .filter(Boolean);
    if (ids.length) {
      enqueueForEpg(ids, true);
      void processEpgQueue();
    }
  }, [focusedChannelIndex, filteredChannels, enqueueForEpg, processEpgQueue]);

  // Reset scroll position and focused index on category switch or search change only.
  // Intentionally NOT watching filteredChannels directly — toggling favorites/schedules
  // would recompute filteredChannels and incorrectly reset focus to the first item.
  useEffect(() => {
    setChannelScrollTop(0);
    if (channelListRef.current) channelListRef.current.scrollTop = 0;
    // If this category change was triggered by initial auto-positioning (to match the
    // playing channel), skip the focus reset so the channel positioning effect can run.
    if (initialCategorySetRef.current) {
      initialCategorySetRef.current = false;
      return;
    }
    // Only reset focus to top if the initial channel has already been positioned once.
    // This prevents overriding the very-first positioning on mount.
    if (positionedChannelRequestRef.current) {
      setFocusedChannelIndex(0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategoryIndex, searchQuery]);

  // Available days for the focused channel, derived from actual loaded EPG data.
  // Falls back to the full candidate window while EPG is still downloading.
  // Window: today to +7 days.
  const availableDays = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const candidateDays: { date: Date; label: string; weekday: string }[] = [];
    for (let i = 0; i <= 6; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      candidateDays.push({ date: d, label: formatDate(d), weekday: t(`liveTV.days.${WEEKDAYS[d.getDay()]}`) });
    }
    const programs = epgByStreamId[focusedStreamId] || [];
    if (!programs.length) return candidateDays;
    const programDayKeys = new Set(programs.map(p => toProgramDayKey(p.startMs)));
    const withData = candidateDays.filter(d => programDayKeys.has(toDayKey(d.date)));
    return withData.length ? withData : candidateDays;
  }, [epgByStreamId, focusedStreamId, t]);

  availableDaysLengthRef.current = availableDays.length;

  // Clamp selected day index when available days shrink after EPG loads
  useEffect(() => {
    setSelectedDayIndex(prev => Math.min(prev, Math.max(0, availableDays.length - 1)));
  }, [availableDays.length]);

  // Reset day selection to today when the focused channel changes
  useEffect(() => {
    if (!focusedStreamId) return;
    setSelectedDayIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedStreamId]);

  const selectedDay = availableDays[selectedDayIndex] || availableDays[0];
  const selectedDayKey = selectedDay ? toDayKey(selectedDay.date) : toDayKey(new Date());

  const epgProgramsForDay = useMemo(() => {
    // Na categoria Agendamentos: mostra os programas agendados do canal focado, ordenados por data
    if (selectedCategoryNode?.kind === 'fixed' && selectedCategoryNode.id === 'schedule') {
      return schedules
        .filter((s) => s.streamId === focusedStreamId)
        .map((s) => ({
          startMs: s.startMs,
          endMs: s.endMs,
          title: s.title,
          description: s.description || '',
        } as XtreamCurrentLiveProgram))
        .sort((a, b) => a.startMs - b.startMs);
    }
    const schedule = epgByStreamId[focusedStreamId] || [];
    return schedule.filter((program) => toProgramDayKey(program.startMs) === selectedDayKey);
  }, [epgByStreamId, selectedDayKey, focusedStreamId, selectedCategoryNode, schedules]);

  useEffect(() => {
    setSelectedProgramIndex((prev) => {
      if (!epgProgramsForDay.length) return 0;
      return Math.min(prev, epgProgramsForDay.length - 1);
    });
  }, [epgProgramsForDay]);

  const selectedProgram = epgProgramsForDay[selectedProgramIndex] || null;
  const currentProgram = (epgByStreamId[focusedStreamId] || []).find((program) => {
    const now = Date.now();
    return now >= program.startMs && now < program.endMs;
  }) || null;

  const playingCurrentProgram = useMemo(() => {
    if (!playingChannel) return null;
    const playingId = String(playingChannel.stream_id || '');
    const programs = epgByStreamId[playingId] || [];
    const nowMs = Date.now();
    return programs.find((p) => nowMs >= p.startMs && nowMs < p.endMs) || null;
  }, [playingChannel, epgByStreamId]);

  const playingNextProgram = useMemo(() => {
    if (!playingChannel) return null;
    const playingId = String(playingChannel.stream_id || '');
    const programs = epgByStreamId[playingId] || [];
    const nowMs = Date.now();
    const currentIdx = programs.findIndex((p) => nowMs >= p.startMs && nowMs < p.endMs);
    if (currentIdx >= 0 && currentIdx + 1 < programs.length) return programs[currentIdx + 1];
    return null;
  }, [playingChannel, epgByStreamId]);

  const resolutionLabel = useMemo(() => {
    if (!videoResolution) return null;
    const { height } = videoResolution;
    if (height >= 2160) return '4K';
    if (height >= 1080) return 'FHD';
    if (height >= 720) return 'HD';
    return 'SD';
  }, [videoResolution]);

  const playingProgress = useMemo(() => {
    if (!playingCurrentProgram) return -1;
    const nowMs = Date.now();
    return Math.min(100, Math.max(0,
      ((nowMs - playingCurrentProgram.startMs) / (playingCurrentProgram.endMs - playingCurrentProgram.startMs)) * 100
    ));
  }, [playingCurrentProgram]);

  const timeshiftTimeline = useMemo(() => {
    if (!timeshiftInfo) return null;
    const nowMs = Date.now();
    const historyStartMs = nowMs - (timeshiftInfo.liveEdge - timeshiftInfo.windowStart) * 1000;
    const playbackMs = nowMs - (timeshiftInfo.liveEdge - timeshiftInfo.currentTime) * 1000;
    const startMs = playingCurrentProgram ? Math.min(playingCurrentProgram.startMs, historyStartMs) : historyStartMs;
    const endMs = playingCurrentProgram ? Math.max(playingCurrentProgram.endMs, nowMs) : nowMs;
    const duration = Math.max(1, endMs - startMs);
    const percent = (value: number) => Math.max(0, Math.min(100, ((value - startMs) / duration) * 100));
    const historyStart = percent(historyStartMs);
    const playback = percent(playbackMs);
    const live = percent(nowMs);
    return {
      historyStart,
      historyWidth: Math.max(0, playback - historyStart),
      aheadWidth: Math.max(0, live - playback),
      playback,
      live,
      historySeconds: timeshiftInfo.currentTime - timeshiftInfo.windowStart,
      aheadSeconds: timeshiftInfo.liveEdge - timeshiftInfo.currentTime,
    };
  }, [playingCurrentProgram, timeshiftInfo]);

  const connectionMbps = throughputMbps !== null ? `${throughputMbps.toFixed(1)} Mbps` : '-- Mbps';

  const now = new Date(clockNow);
  const nowClock = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

  const categoryLabel = selectedCategoryNode?.label || t('liveTV.allChannels');

  const toggleCurrentFavorite = useCallback(() => {
    if (!focusedChannel || !focusedStreamId) return;

    toggleFavorite({
      id: focusedStreamId,
      type: 'live',
      name: focusedChannel.name,
      icon: focusedChannel.icon || focusedChannel.stream_icon,
      categoryId: focusedChannel.category_id,
      streamId: focusedStreamId,
      containerExtension: focusedChannel.container_extension,
      isKids: isKidsCategory(categories.find(c => c.id === focusedChannel.category_id)?.name ?? ''),
      updatedAt: Date.now(),
    });
  }, [focusedChannel, focusedStreamId, categories, toggleFavorite]);

  const toggleProgramSchedule = useCallback((program: XtreamCurrentLiveProgram) => {
    if (program.startMs <= Date.now() && !isScheduled(focusedStreamId, program.startMs)) return;
    toggleSchedule({
      streamId: focusedStreamId,
      categoryId: String(focusedChannel?.category_id || ''),
      channelName: focusedChannel?.name || '',
      channelIcon: focusedChannel?.icon || focusedChannel?.stream_icon,
      title: program.title,
      description: program.description,
      startMs: program.startMs,
      endMs: program.endMs,
      addedAt: Date.now(),
    });
  }, [focusedChannel, focusedStreamId, isScheduled, toggleSchedule]);

  const handleThroughputMbps = useCallback((mbps: number | null) => {
    setThroughputMbps((prev) => {
      if (mbps === null) return null;
      if (prev === null) return mbps;
      return Math.abs(prev - mbps) >= 0.15 ? mbps : prev;
    });
  }, []);

  useEffect(() => {
    if (focusArea !== 'categories') return;
    const element = categoryItemRefs.current[selectedCategoryIndex];
    element?.scrollIntoView({ block: 'nearest' });
  }, [focusArea, selectedCategoryIndex, categoryNodes.length]);

  // Auto-focus search input when search category is selected + categories column is open
  useEffect(() => {
    if (selectedCategoryNode?.id === 'search' && focusArea === 'categories') {
      const timer = setTimeout(() => searchInputRef.current?.focus(), 80);
      return () => clearTimeout(timer);
    }
  }, [selectedCategoryNode?.id, focusArea]);

  // Clear search query when leaving the search category
  useEffect(() => {
    if (selectedCategoryNode?.id !== 'search') {
      setSearchQuery('');
    }
  }, [selectedCategoryNode?.id]);

  // Clear search query when the component goes inactive (user navigated away)
  useEffect(() => {
    if (!isActive) {
      setSearchQuery('');
      searchInputRef.current?.blur();
    }
  }, [isActive]);

  useEffect(() => {
    if (focusArea !== 'channels') return;
    const container = channelListRef.current;
    if (!container) return;
    const itemTop = focusedChannelIndex * CHANNEL_ITEM_H;
    const itemBottom = itemTop + CHANNEL_ITEM_H;
    if (itemTop < container.scrollTop) {
      const newTop = itemTop;
      isProgrammaticScrollRef.current = true;
      container.scrollTop = newTop;
      setChannelScrollTop(newTop);
    } else if (itemBottom > container.scrollTop + container.clientHeight) {
      const newTop = itemBottom - container.clientHeight;
      isProgrammaticScrollRef.current = true;
      container.scrollTop = newTop;
      setChannelScrollTop(newTop);
    }
  }, [focusArea, focusedChannelIndex, filteredChannels.length]);

  useEffect(() => {
    if (focusArea !== 'epg-days') return;
    const element = epgDayRefs.current[selectedDayIndex];
    element?.scrollIntoView({ block: 'nearest' });
  }, [focusArea, selectedDayIndex, availableDays.length]);

  useEffect(() => {
    if (focusArea !== 'epg-list') return;
    const element = epgItemRefs.current[selectedProgramIndex];
    element?.scrollIntoView({ block: 'nearest' });
  }, [focusArea, selectedProgramIndex, epgProgramsForDay.length]);

  // When the selected day is today, always snap the current program to the top of the EPG list.
  // requestAnimationFrame ensures refs are populated after the render that drew the items.
  useEffect(() => {
    const todayKey = toDayKey(new Date());
    if (selectedDayKey !== todayKey) return;
    const now = Date.now();
    const currentIdx = epgProgramsForDay.findIndex((p) => now >= p.startMs && now < p.endMs);
    if (currentIdx < 0) return;
    requestAnimationFrame(() => {
      epgItemRefs.current[currentIdx]?.scrollIntoView({ block: 'start' });
    });
  }, [selectedDayKey, epgProgramsForDay]);

  const resetFsoTimer = useCallback(() => {
    if (fsoHideTimerRef.current) clearTimeout(fsoHideTimerRef.current);
    fsoHideTimerRef.current = setTimeout(() => {
      setFsoVisible(false);
      setFsoFocusedBtn(null);
    }, 5000);
  }, []);

  const showUnavailable = useCallback(() => {
    if (mediaKeyMessageTimerRef.current) clearTimeout(mediaKeyMessageTimerRef.current);
    setMediaKeyMessage(t('playback.functionUnavailable'));
    mediaKeyMessageTimerRef.current = setTimeout(() => setMediaKeyMessage(null), 3000);
  }, [t]);

  const openTrackDialog = useCallback((kind: 'audio' | 'subtitle') => {
    const index = kind === 'audio'
      ? Math.max(0, audioTracks.findIndex(track => track.id === activeAudioId))
      : Math.max(0, subtitleTracks.findIndex(track => track.id === activeSubtitleId) + 1);
    setTrackDialogIndex(index);
    setTrackDialog(kind);
    setFsoVisible(true);
    if (fsoHideTimerRef.current) clearTimeout(fsoHideTimerRef.current);
  }, [activeAudioId, activeSubtitleId, audioTracks, subtitleTracks]);

  const selectTrack = useCallback((kind: 'audio' | 'subtitle', trackId: number) => {
    const operation = kind === 'audio'
      ? miniPlayerRef.current?.selectAudioTrack(trackId)
      : miniPlayerRef.current?.selectSubtitleTrack(trackId);
    setTrackDialog(null);
    resetFsoTimer();
    if (!operation) return;
    if (trackSwitchTimerRef.current) clearTimeout(trackSwitchTimerRef.current);
    trackSwitchTimerRef.current = setTimeout(() => setTrackSwitchMessage(t('playback.switchingTrack')), 500);
    operation.then(() => {
      if (trackSwitchTimerRef.current) clearTimeout(trackSwitchTimerRef.current);
      trackSwitchTimerRef.current = null;
      setTrackSwitchMessage(null);
    }).catch(() => {
      if (trackSwitchTimerRef.current) clearTimeout(trackSwitchTimerRef.current);
      setTrackSwitchMessage(t('playback.trackError'));
      trackSwitchTimerRef.current = setTimeout(() => setTrackSwitchMessage(null), 3000);
    });
  }, [resetFsoTimer, t]);

  useEffect(() => () => {
    if (trackSwitchTimerRef.current) clearTimeout(trackSwitchTimerRef.current);
    if (mediaKeyMessageTimerRef.current) clearTimeout(mediaKeyMessageTimerRef.current);
  }, []);

  useEffect(() => {
    if (!isActive) return;
    const onMouseClick = () => {
      if (columnsHidden) {
        setColumnsHidden(false);
        setFsoVisible(false);
        setFsoFocusedBtn(null);
        if (fsoHideTimerRef.current) clearTimeout(fsoHideTimerRef.current);
      }
    };
    window.addEventListener('click', onMouseClick);
    return () => window.removeEventListener('click', onMouseClick);
  }, [columnsHidden, isActive]);

  useEffect(() => {
    if (!isActive) return;
    const onKeyDown = (event: KeyboardEvent) => {
      const mediaAction = getMediaKeyAction(event);
      if (mediaAction) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (mediaAction === 'play') miniPlayerRef.current?.play();
        else if (mediaAction === 'pause') miniPlayerRef.current?.pause();
        else if (mediaAction === 'playPause') miniPlayerRef.current?.togglePlayPause();
        else if (mediaAction === 'stop') {
          miniPlayerRef.current?.stop();
          if (columnsHidden) {
            const currentPlayingIdx = filteredChannels.findIndex(
              (channel) => String(channel.stream_id || '') === playingStreamId
            );
            if (currentPlayingIdx >= 0) setFocusedChannelIndex(currentPlayingIdx);
            setFocusArea('channels');
            setColumnsHidden(false);
            setFsoVisible(false);
            setFsoFocusedBtn(null);
            if (fsoHideTimerRef.current) clearTimeout(fsoHideTimerRef.current);
          }
        } else {
          const delta = mediaAction === 'rewind' ? -10 : 10;
          if (!columnsHidden || !miniPlayerRef.current?.seekLiveBy(delta)) showUnavailable();
          else {
            setFsoVisible(true);
            setFsoFocusedBtn(null);
            resetFsoTimer();
          }
        }
        return;
      }

      if (trackDialog) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const optionCount = trackDialog === 'audio' ? audioTracks.length : subtitleTracks.length + 1;
        if (event.key === 'ArrowUp' || event.key === 'ArrowLeft' || event.key === 'ArrowDown' || event.key === 'ArrowRight') {
          const delta = event.key === 'ArrowUp' || event.key === 'ArrowLeft' ? -1 : 1;
          setTrackDialogIndex(index => Math.max(0, Math.min(optionCount - 1, index + delta)));
        } else if (event.key === 'Enter' || event.key === 'Return' || event.key === ' ') {
          const trackId = trackDialog === 'audio'
            ? audioTracks[trackDialogIndex]?.id
            : (trackDialogIndex === 0 ? -1 : subtitleTracks[trackDialogIndex - 1]?.id);
          if (typeof trackId === 'number') selectTrack(trackDialog, trackId);
        } else if (isBackKey(event)) {
          setTrackDialog(null);
          resetFsoTimer();
        }
        return;
      }
      // When search input is focused, pass keys through except navigation
      if (document.activeElement === searchInputRef.current) {
        if (event.key === 'Escape') {
          event.preventDefault();
          searchInputRef.current?.blur();
          setSearchQuery('');
          setFocusArea('categories');
          setColumnsHidden(true);
          setFsoVisible(false);
          setFsoFocusedBtn(null);
          if (fsoHideTimerRef.current) clearTimeout(fsoHideTimerRef.current);
        } else if (event.key === 'Enter') {
          event.preventDefault();
          searchInputRef.current?.blur();
          setFocusArea('channels');
          setFocusedChannelIndex(0);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          searchInputRef.current?.blur();
          setFocusArea('channels');
          setFocusedChannelIndex(0);
        } else if (event.key === 'ArrowDown') {
          event.preventDefault();
          searchInputRef.current?.blur();
          setSearchQuery('');
          setFocusArea('categories');
          // move selection to the next category below search
          setSelectedCategoryIndex(prev => Math.min(prev + 1, categoryNodes.length - 1));
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          event.preventDefault(); // swallow — do nothing
        }
        return;
      }

      if (isBackKey(event)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (event.repeat) return;
        if (categoryBlockMode) {
          setShowCategorySaveModal(true);
          return;
        }
        // If search input is focused, just blur it — don't leave the page
        if (document.activeElement === searchInputRef.current) {
          searchInputRef.current?.blur();
          return;
        }
        if (epgPanelOpen) {
          setEpgPanelOpen(false);
          setFocusArea('favorite');
          return;
        }
        if (columnsHidden) {
          if (fsoVisible) {
            setFsoVisible(false); setFsoFocusedBtn(null);
            if (fsoHideTimerRef.current) clearTimeout(fsoHideTimerRef.current);
            return;
          }
          onBack();
          return;
        }
        setColumnsHidden(true);
        setFsoVisible(false);
        setFsoFocusedBtn(null);
        if (fsoHideTimerRef.current) clearTimeout(fsoHideTimerRef.current);
        return;
      }

      if (columnsHidden) {
        // Up/Down only reveals/keeps the fullscreen controls visible.
        if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
          event.preventDefault();
          setFsoVisible(true);
          resetFsoTimer();
          return;
        }
        // Left/Right seeks Live TV; after Enter selects the actions, it navigates buttons.
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          if (fsoVisible && fsoFocusedBtn) {
            const btns: Array<'pip' | 'lock'> = [];
            if (desktopPictureInPicture) btns.push('pip');
            btns.push('lock');
            const currentBtnIdx = fsoFocusedBtn ? btns.indexOf(fsoFocusedBtn) : -1;
            const step = event.key === 'ArrowRight' ? 1 : -1;
            const newBtnIdx = currentBtnIdx + step;
            if (newBtnIdx < 0) setFsoFocusedBtn(null);
            else if (newBtnIdx >= btns.length) setFsoFocusedBtn(btns[btns.length - 1]);
            else setFsoFocusedBtn(btns[newBtnIdx]);
            resetFsoTimer();
            return;
          }
          if (miniPlayerRef.current?.seekLiveBy(event.key === 'ArrowLeft' ? -10 : 10)) {
            setFsoVisible(true);
            setFsoFocusedBtn(null);
            resetFsoTimer();
            return;
          }
          showUnavailable();
          return;
        }
        // Enter/Space → activate focused button or restore columns
        if (event.key === 'Enter' || event.key === 'Return' || event.key === ' ') {
          event.preventDefault();
          if (fsoVisible && fsoFocusedBtn === 'pip') {
            void miniPlayerRef.current?.togglePictureInPicture().catch(() => {});
            resetFsoTimer();
            return;
          }
          if (fsoVisible && fsoFocusedBtn === 'lock') {
            requestChannelLockToggle();
            resetFsoTimer();
            return;
          }
          if (fsoVisible) {
            if (desktopPictureInPicture) setFsoFocusedBtn('pip');
            else setFsoFocusedBtn('lock');
            resetFsoTimer();
            return;
          }
          setColumnsHidden(false);
          setFsoVisible(false);
          setFsoFocusedBtn(null);
          if (fsoHideTimerRef.current) clearTimeout(fsoHideTimerRef.current);
        }
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (focusArea === 'categories') {
          setSelectedCategoryIndex((prev) => Math.max(0, prev - 1));
          return;
        }
        if (focusArea === 'channels' || focusArea === 'favorite') {
          setFocusedChannelIndex((prev) => Math.max(0, prev - 1));
          if (focusArea === 'favorite') setFocusArea('channels');
          return;
        }
        if (focusArea === 'epg-days') {
          setSelectedDayIndex((prev) => Math.max(0, prev - 1));
          return;
        }
        if (focusArea === 'epg-list') {
          setSelectedProgramIndex((prev) => Math.max(0, prev - 1));
        }
        if (focusArea === 'epg-schedule') {
          setFocusArea('epg-list');
          setSelectedProgramIndex((prev) => Math.max(0, prev - 1));
        }
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (focusArea === 'categories') {
          setSelectedCategoryIndex((prev) => Math.min(categoryNodes.length - 1, prev + 1));
          return;
        }
        if (focusArea === 'channels' || focusArea === 'favorite') {
          setFocusedChannelIndex((prev) => Math.min(Math.max(filteredChannels.length - 1, 0), prev + 1));
          if (focusArea === 'favorite') setFocusArea('channels');
          return;
        }
        if (focusArea === 'epg-days') {
          setSelectedDayIndex((prev) => Math.min(Math.max(availableDaysLengthRef.current - 1, 0), prev + 1));
          return;
        }
        if (focusArea === 'epg-list') {
          setSelectedProgramIndex((prev) => Math.min(Math.max(epgProgramsForDay.length - 1, 0), prev + 1));
        }
        if (focusArea === 'epg-schedule') {
          setFocusArea('epg-list');
          setSelectedProgramIndex((prev) => Math.min(Math.max(epgProgramsForDay.length - 1, 0), prev + 1));
        }
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        if (categoryBlockMode) return;
        if (focusArea === 'channels') {
          setFocusArea('categories');
          return;
        }
        if (focusArea === 'favorite') {
          setFocusArea('channels');
          return;
        }
        if (focusArea === 'epg-days') {
          setEpgPanelOpen(false);
          setFocusArea('favorite');
          return;
        }
        if (focusArea === 'epg-list') {
          setFocusArea('epg-days');
        }
        if (focusArea === 'epg-schedule') {
          setFocusArea('epg-list');
        }
        return;
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        if (categoryBlockMode) return;
        if (focusArea === 'categories') {
          setFocusArea('channels');
          return;
        }
        if (focusArea === 'channels') {
          setFocusArea('favorite');
          return;
        }
        if (focusArea === 'favorite') {
          // Snap EPG focus to today + current program before opening
          const todayKey = toDayKey(new Date());
          const todayIdx = availableDays.findIndex((d) => toDayKey(d.date) === todayKey);
          if (todayIdx >= 0) setSelectedDayIndex(todayIdx);
          const schedule = epgByStreamId[focusedStreamId] || [];
          const nowMs = Date.now();
          const curProgIdx = schedule
            .filter((p) => toProgramDayKey(p.startMs) === todayKey)
            .findIndex((p) => nowMs >= p.startMs && nowMs < p.endMs);
          if (curProgIdx >= 0) setSelectedProgramIndex(curProgIdx);
          setEpgPanelOpen(true);
          setFocusArea('epg-days');
          return;
        }
        if (focusArea === 'epg-days') {
          setFocusArea('epg-list');
        }
        if (focusArea === 'epg-list') {
          // Move focus to the schedule button of the selected program
          setFocusArea('epg-schedule');
        }
        return;
      }

      if (event.key === 'Enter' || event.key === 'Return' || event.key === ' ') {
        event.preventDefault();
        if (focusArea === 'categories' && categoryBlockMode && selectedCategoryNode?.kind === 'dynamic') {
          toggleDraftCategory(selectedCategoryNode.categoryId);
          return;
        }
        if (focusArea === 'categories' && selectedCategoryNode?.id === 'blockCategories') {
          requestParentalAction({ type: 'editCategories' });
          return;
        }
        if (focusArea === 'categories' && selectedCategoryNode?.id === 'search') {
          setTimeout(() => searchInputRef.current?.focus(), 0);
          return;
        }
        if (focusArea === 'channels') {
          const channelToPlay = filteredChannels[focusedChannelIndex] || null;
          if (channelToPlay) requestChannelPlayback(channelToPlay, true);
          return;
        }

        if (focusArea === 'favorite') {
          toggleCurrentFavorite();
          return;
        }

        if (focusArea === 'epg-list') {
          const program = epgProgramsForDay[selectedProgramIndex];
          if (program) toggleProgramSchedule(program);
          return;
        }

        if (focusArea === 'epg-schedule') {
          // Toggle schedule for the currently selected program
          const program = epgProgramsForDay[selectedProgramIndex];
          if (program) toggleProgramSchedule(program);
        }
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    availableDays,
    activeAudioId,
    activeSubtitleId,
    audioTracks,
    categoryBlockMode,
    categoryNodes.length,
    columnsHidden,
    epgByStreamId,
    epgPanelOpen,
    desktopPictureInPicture,
    epgProgramsForDay,
    filteredChannels,
    filteredChannels.length,
    focusedChannelIndex,
    focusedStreamId,
    focusArea,
    focusedChannel,
    fsoFocusedBtn,
    fsoVisible,
    isActive,
    onBack,
    playingStreamId,
    requestChannelLockToggle,
    requestChannelPlayback,
    requestParentalAction,
    resetFsoTimer,
    showUnavailable,
    openTrackDialog,
    selectTrack,
    selectedCategoryNode,
    selectedProgramIndex,
    subtitleTracks,
    trackDialog,
    trackDialogIndex,
    toggleCurrentFavorite,
    toggleProgramSchedule,
    toggleDraftCategory,
  ]);

  const compactCategories = focusArea !== 'categories';

  const channelNameMaxChars = useMemo(() => {
    if (typeof document === 'undefined') return 25;
    const value = Number.parseInt(
      getComputedStyle(document.documentElement).getPropertyValue('--channel-name-max-chars').trim(),
      10
    );
    return Number.isFinite(value) && value > 0 ? value : 25;
  }, []);

  const compactIconSize = useMemo(() => {
    if (typeof document === 'undefined') return 32;
    const raw = getComputedStyle(document.documentElement)
      .getPropertyValue('--livetv-compact-icon-size').trim();
    const value = Number.parseInt(raw, 10);
    return Number.isFinite(value) && value > 0 ? value : 32;
  }, []);

  const fixedIcon = (id: FixedCategoryId) => {
    const iconSize = compactCategories ? compactIconSize : 20;
    if (id === 'search') return <MdSearch size={iconSize} />;
    if (id === 'schedule') return <MdEvent size={iconSize} />;
    if (id === 'favorites') return <MdFavoriteBorder size={iconSize} />;
    if (id === 'blockCategories') return <MdBlock size={iconSize} />;
    return <MdList size={iconSize} />;
  };

  const categoriesColumnWidth = useMemo(() => {
    if (typeof document === 'undefined') return 270;

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return 270;

    context.font = '400 24px Roboto, sans-serif';

    const longestLabelWidth = categoryNodes.reduce((max, node) => {
      const width = context.measureText(node.label || '').width;
      return Math.max(max, width);
    }, 0);

    // 10+10 category item horizontal padding + 24 icon + 10 gap + 12 breathing room
    return Math.max(270, Math.ceil(longestLabelWidth + 66));
  }, [categoryNodes]);

  const channelsOpenWidthFor25Chars = useMemo(() => {
    if (typeof document === 'undefined') return 430;

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return 430;

    context.font = '400 22px Roboto, sans-serif';
    const labelWidth = context.measureText(`${'A'.repeat(channelNameMaxChars)}...`).width;

    // 52 logo + 10 gap + text area(25+...) + 46 favorite + 20 row padding + safety
    return Math.ceil(52 + 10 + labelWidth + 46 + 20 + 26);
  }, [channelNameMaxChars]);

  const rootStyle = useMemo(
    () => ({
      '--categories-col-width-full': `${categoriesColumnWidth}px`,
      '--channels-col-width-open-categories': `${channelsOpenWidthFor25Chars}px`,
    } as React.CSSProperties),
    [categoriesColumnWidth, channelsOpenWidthFor25Chars]
  );

  // In kids mode, check whether the server has any kids-category channels.
  // If not, auto-back and show nothing (channel list is not available for kids).
  const kidsCategories = useMemo(
    () => isKidsMode ? categories.filter(c => isKidsCategory(c.name)) : null,
    [categories, isKidsMode]
  );

  const noKidsChannels = isKidsMode && !loading && kidsCategories !== null && kidsCategories.length === 0;

  // Auto-back when kids mode is on but no kids categories found on this server
  useEffect(() => {
    if (noKidsChannels) {
      const t = setTimeout(() => onBack(), 0);
      return () => clearTimeout(t);
    }
  }, [noKidsChannels, onBack]);

  return (
    isBlocked('liveTV') ? (
      <div className="livetvcat livetvcat--blocked">
        <p className="livetvcat__blocked-msg">{t('parental.blockedMessage')}</p>
      </div>
    ) : noKidsChannels ? (
      <div className="livetvcat livetvcat--blocked">
        <p className="livetvcat__blocked-msg">{t('parental.blockedMessage')}</p>
      </div>
    ) :
    <div
      className={`livetvcat${homePreview ? ' is-home-preview' : ''}${compactCategories ? ' is-categories-compact' : ' is-categories-open'}${epgPanelOpen ? ' is-epg-open' : ''}${columnsHidden ? ' is-columns-hidden' : ''}${columnsHidden && fsoVisible ? ' is-fso-visible' : ''}`}
      style={rootStyle}
      onClick={() => {
        if (!columnsHidden) {
          setColumnsHidden(true);
          setFsoVisible(false);
          setFsoFocusedBtn(null);
          if (fsoHideTimerRef.current) clearTimeout(fsoHideTimerRef.current);
        }
      }}
    >
      <div className="livetvcat__video-bg">
        <MiniPlayer
          ref={miniPlayerRef}
          channel={playbackActive ? playbackChannel : null}
          recordLiveHistory={isActive}
          onThroughputMbps={handleThroughputMbps}
          onResolutionChange={(w, h) => setVideoResolution({ width: w, height: h })}
          onEngineChange={setPlaybackEngine}
          onTimeshiftChange={setTimeshiftInfo}
          onAudioTracksChange={(tracks, activeId) => { setAudioTracks(tracks); setActiveAudioId(activeId); }}
          onSubtitleTracksChange={(tracks, activeId) => { setSubtitleTracks(tracks); setActiveSubtitleId(activeId); }}
        />
        {mediaKeyMessage && <PlaybackStatusOverlay status="error" message={mediaKeyMessage} />}
      </div>

      {trackSwitchMessage && (
        <div className="livetvcat__track-status" aria-live="polite">{trackSwitchMessage}</div>
      )}

      {trackDialog && (
        <div className="livetvcat__track-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
          <div className="livetvcat__track-modal-title">{t(trackDialog === 'audio' ? 'liveTV.audio' : 'playback.subtitles')}</div>
          <div className="livetvcat__track-options">
            {trackDialog === 'subtitle' && (
              <button
                type="button"
                className={`livetvcat__track-option${trackDialogIndex === 0 ? ' livetvcat__track-option--focused' : ''}${activeSubtitleId === -1 ? ' livetvcat__track-option--active' : ''}`}
                onClick={() => selectTrack('subtitle', -1)}
              ><span>{t('liveTV.subtitleOff')}</span><span>{activeSubtitleId === -1 ? '✓' : ''}</span></button>
            )}
            {(trackDialog === 'audio' ? audioTracks : subtitleTracks).map((track, index) => {
              const optionIndex = trackDialog === 'audio' ? index : index + 1;
              const active = trackDialog === 'audio' ? track.id === activeAudioId : track.id === activeSubtitleId;
              return (
                <button
                  key={`${trackDialog}-${track.id}`}
                  type="button"
                  className={`livetvcat__track-option${trackDialogIndex === optionIndex ? ' livetvcat__track-option--focused' : ''}${active ? ' livetvcat__track-option--active' : ''}`}
                  onClick={() => selectTrack(trackDialog, track.id)}
                >
                  <span>{track.name || t(trackDialog === 'audio' ? 'playback.audioTrack' : 'playback.subtitleTrack', { num: index + 1 })}{'forced' in track && track.forced ? <span className="livetvcat__track-forced">{t('playback.forcedSubtitle')}</span> : null}</span>
                  <span>{active ? '✓' : ''}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Fullscreen overlay (vco-style) ── */}
      <div className="livetvcat__fso" onClick={(e) => e.stopPropagation()}>
        {/* Gradient scrim */}
        <div className="livetvcat__fso-gradient" />

        {/* HUD anchored to bottom */}
        <div className="livetvcat__fso-hud">

          {/* Row 1: title + action buttons */}
          <div className="livetvcat__fso-info-row">

            {/* Left: logo + channel name + current program */}
            <div className="livetvcat__fso-title-wrap">
              <div className="livetvcat__fso-title-line">
                {(playingChannel?.icon || playingChannel?.stream_icon) && (
                  <img
                    src={playingChannel.icon || playingChannel.stream_icon}
                    alt=""
                    loading="lazy"
                    className="livetvcat__fso-logo-img"
                  />
                )}
                <span className="livetvcat__fso-channel-name">{playingChannel?.name || ''}</span>
              </div>
              {playingCurrentProgram && (
                <div className="livetvcat__fso-subtitle">
                  <span className="livetvcat__fso-time-range">
                    {formatHHMM(playingCurrentProgram.startMs)} – {formatHHMM(playingCurrentProgram.endMs)}
                  </span>
                  <span className="livetvcat__fso-program-title">{playingCurrentProgram.title}</span>
                </div>
              )}
            </div>

            {/* Right: badges + action buttons */}
            <div className="livetvcat__fso-actions">
              <span className="livetvcat__fso-clock" title={t('playback.localTime')}>
                {nowClock}
              </span>

              {/* Resolution badge */}
              {resolutionLabel && (
                <span className="livetvcat__fso-res-badge" title={t('liveTV.resolution')}>
                  {resolutionLabel}
                </span>
              )}

              {playbackEngine && (
                <span
                  className={`livetvcat__fso-engine-badge livetvcat__fso-engine-badge--${playbackEngine}`}
                  title={t('playback.engine')}
                >
                  {playbackEngine === 'avplay' ? 'AVPLAY' : playbackEngine === 'hls' ? 'HLS.JS' : playbackEngine === 'native-hls' ? t('playback.nativeHls') : 'MPEG-TS'}
                </span>
              )}

              {desktopPictureInPicture && (
                <button
                  type="button"
                  className={`livetvcat__fso-btn${fsoFocusedBtn === 'pip' ? ' livetvcat__fso-btn--focused' : ''}`}
                  title={t('playback.pictureInPicture')}
                  aria-label={t('playback.openPictureInPicture')}
                onClick={() => { void miniPlayerRef.current?.togglePictureInPicture().catch(() => {}); }}
                >
                  <MdPictureInPictureAlt className="livetvcat__fso-icon" />
                </button>
              )}

              {/* Parental lock */}
              <button
                type="button"
                className={`livetvcat__fso-btn${channelLocked ? ' livetvcat__fso-btn--active' : ''}${fsoFocusedBtn === 'lock' ? ' livetvcat__fso-btn--focused' : ''}`}
                title={channelLocked ? t('liveTV.unlockChannel') : t('liveTV.lockChannel')}
                onClick={requestChannelLockToggle}
              >
                {channelLocked
                  ? <MdLock className="livetvcat__fso-icon" />
                  : <MdLockOpen className="livetvcat__fso-icon" />}
              </button>


            </div>
          </div>

          {/* Row 2: EPG progress + next program */}
          <div className="livetvcat__fso-epg-row">
            {timeshiftTimeline && (
              <span className="livetvcat__fso-rewind-label">−{formatOffset(timeshiftTimeline.historySeconds)}</span>
            )}
            <div className={`livetvcat__fso-progress-track${playingCurrentProgram ? ' has-epg' : ''}`}>
              {timeshiftTimeline ? (
                <>
                  {playingCurrentProgram && (
                    <div
                      className="livetvcat__fso-progress-epg"
                      style={{ width: `${timeshiftTimeline.live}%` }}
                    />
                  )}
                  <div
                    className="livetvcat__fso-progress-history"
                    style={{ left: `${timeshiftTimeline.historyStart}%`, width: `${timeshiftTimeline.historyWidth}%` }}
                  />
                  <div
                    className="livetvcat__fso-progress-ahead"
                    style={{ left: `${timeshiftTimeline.playback}%`, width: `${timeshiftTimeline.aheadWidth}%` }}
                  />
                  <div
                    className="livetvcat__fso-progress-pin"
                    style={{ left: `${timeshiftTimeline.playback}%` }}
                  />
                </>
              ) : playingProgress >= 0 ? (
                <div
                  className="livetvcat__fso-progress-fill"
                  style={{ width: `${playingProgress}%` }}
                />
              ) : null}
            </div>
            {timeshiftTimeline && (
              <span className="livetvcat__fso-forward-label">+{formatOffset(timeshiftTimeline.aheadSeconds)}</span>
            )}
            <span className="livetvcat__fso-live-badge">{t('liveTV.live')}</span>
            {playingNextProgram && (
              <div className="livetvcat__fso-next-wrap">
                <span className="livetvcat__fso-next-label">{t('liveTV.nextProgram')}</span>
                <span className="livetvcat__fso-next-title">
                  {formatHHMM(playingNextProgram.startMs)} · {playingNextProgram.title}
                </span>
              </div>
            )}
          </div>

        </div>
      </div>

      <div className="livetvcat__overlay-grid" onClick={(e) => e.stopPropagation()}>
      <div className={`livetvcat__categories ${compactCategories ? 'is-compact' : ''}`}>
        {categoryNodes.map((node, index) => {
          const selected = index === selectedCategoryIndex;
          const focused = focusArea === 'categories' && selected;
          const isSearchNode = node.kind === 'fixed' && node.id === 'search';
          const categoryBlocked = node.kind === 'dynamic' && (
            categoryBlockMode ? draftBlockedCategoryIds.has(node.categoryId) : isCategoryMarked(node.categoryId)
          );
          return (
            <div
              key={node.id}
              className={`livetvcat__category-item${selected ? ' is-selected' : ''}${focused ? ' is-focused' : ''}`}
              ref={(element) => {
                categoryItemRefs.current[index] = element;
              }}
              onClick={() => {
                setFocusArea('categories');
                setSelectedCategoryIndex(index);
                if (categoryBlockMode && node.kind === 'dynamic') {
                  toggleDraftCategory(node.categoryId);
                  return;
                }
                if (node.kind === 'fixed' && node.id === 'blockCategories') {
                  requestParentalAction({ type: 'editCategories' });
                  return;
                }
                if (isSearchNode) {
                  setTimeout(() => searchInputRef.current?.focus(), 50);
                }
              }}
            >
              {node.kind === 'fixed'
                ? <span className="livetvcat__category-icon">{fixedIcon(node.id)}</span>
                : <span className="livetvcat__category-icon-empty">{categoryBlocked && <MdLock className="livetvcat__parental-lock-icon" />}</span>}
              {isSearchNode && !compactCategories
                ? <input
                    ref={searchInputRef}
                    className="livetvcat__search-input"
                    type="text"
                    placeholder={t('liveTV.searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setFocusedChannelIndex(0); }}
                    onFocus={() => { setFocusArea('categories'); setSelectedCategoryIndex(index); }}
                    onBlur={() => {}}
                    onClick={(e) => e.stopPropagation()}
                  />
                : <span className="livetvcat__category-label">{node.label}</span>
              }
            </div>
          );
        })}
      </div>

      <div className="livetvcat__channels">
        <div className="livetvcat__channels-header">{categoryLabel}</div>
        <div
          className="livetvcat__channels-list"
          ref={channelListRef}
          onScroll={(e) => {
            if (isProgrammaticScrollRef.current) {
              isProgrammaticScrollRef.current = false;
              return;
            }
            setChannelScrollTop(e.currentTarget.scrollTop);
          }}
        >

        {loading && <div className="livetvcat__channels-empty">{t('liveTV.loadingChannels')}</div>}
        {!loading && !filteredChannels.length && (
          <div className="livetvcat__channels-empty">{t('liveTV.noChannelsInCategory')}</div>
        )}

        {!loading && filteredChannels.length > 0 && (() => {
          const containerH = channelListRef.current?.clientHeight ?? 580;
          const vStart = Math.max(0, Math.floor(channelScrollTop / CHANNEL_ITEM_H) - CHANNEL_BUFFER);
          const vEnd = Math.min(filteredChannels.length, Math.ceil((channelScrollTop + containerH) / CHANNEL_ITEM_H) + CHANNEL_BUFFER);
          return (
            <div style={{ height: filteredChannels.length * CHANNEL_ITEM_H, position: 'relative' }}>
              <div style={{ position: 'absolute', top: vStart * CHANNEL_ITEM_H, left: 0, right: 0 }}>
                {filteredChannels.slice(vStart, vEnd).map((channel, localIdx) => {
          const index = vStart + localIdx;
          const streamId = String(channel.stream_id || '');
          const selected = focusArea !== 'categories' && index === focusedChannelIndex;
          const focused = focusArea === 'channels' && selected;
          const favFocused = focusArea === 'favorite' && selected;
          const favorited = isFavorite(streamId, 'live');
          const channelBlocked = isChannelMarked(streamId) || isCategoryMarked(String(channel.category_id || ''));

          const _nowMs = Date.now();
          const _programs = epgByStreamId[streamId] || [];
          const _current = _programs.find(p => _nowMs >= p.startMs && _nowMs < p.endMs);
          const _progress = _current
            ? Math.min(100, Math.max(0, ((_nowMs - _current.startMs) / (_current.endMs - _current.startMs)) * 100))
            : -1;

          return (
            <div
              key={`${streamId}-${channel.name}`}
              className={`livetvcat__channel-item${selected ? ' is-selected' : ''}${focused ? ' is-focused' : ''}`}
              onClick={() => {
                setFocusArea('channels');
                setFocusedChannelIndex(index);
                if (streamId && streamId === playingStreamId) {
                  // Clicking on the already-playing channel hides columns (fullscreen mode)
                  setColumnsHidden(true);
                  setFsoVisible(false);
                } else if (streamId) {
                  requestChannelPlayback(channel, false);
                }
              }}
            >
              <div className="livetvcat__channel-logo-wrap">
                {(channel.icon || channel.stream_icon) ? (
                  <img
                    src={channel.icon || channel.stream_icon}
                    alt=""
                    loading="lazy"
                    className="livetvcat__channel-logo"
                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                  />
                ) : (
                  <div className="livetvcat__channel-logo-placeholder" />
                )}
              </div>

              <div className="livetvcat__channel-main">
                <div className="livetvcat__channel-line1">
                  {channelBlocked && <MdLock className="livetvcat__parental-lock-icon" />}
                  <ScrollingChannelName name={channel.name} focused={focused} />
                </div>
                <div className="livetvcat__channel-line2">
                  <MdChevronRight size={14} />
                  <span>{_current?.title || _programs[0]?.title || t('liveTV.noProgram')}</span>
                </div>
                {_progress >= 0 && (
                  <div className="livetvcat__channel-progress-track">
                    <div className="livetvcat__channel-progress-fill" style={{ width: `${_progress}%` }} />
                  </div>
                )}
              </div>

              <div className="livetvcat__channel-actions">
                <button
                  type="button"
                  className={`livetvcat__favorite-btn${favorited ? ' is-favorited' : ''}${favFocused ? ' is-focused' : ''}`}
                  onClick={() => {
                    setFocusedChannelIndex(index);
                    toggleFavorite({
                      id: streamId,
                      type: 'live',
                      name: channel.name,
                      icon: channel.icon || channel.stream_icon,
                      categoryId: channel.category_id,
                      streamId,
                      containerExtension: channel.container_extension,
                      isKids: isKidsCategory(categories.find(c => c.id === channel.category_id)?.name ?? ''),
                      updatedAt: Date.now(),
                    });
                  }}
                >
                  {favorited ? <MdFavorite size={20} /> : <MdFavoriteBorder size={20} />}
                </button>
                {_programs.length > 0 && <button
                  type="button"
                  className="livetvcat__channel-epg-btn"
                  title={t('liveTV.schedules')}
                  onClick={(event) => {
                    event.stopPropagation();
                    setFocusedChannelIndex(index);
                    setEpgPanelOpen(true);
                    setFocusArea('epg-days');
                  }}
                >
                  <span className="livetvcat__channel-epg-arrow" />
                </button>}
              </div>
            </div>
          );
        })}
              </div>
            </div>
          );
        })()}
      </div>
  </div>
      
      <div className="livetvcat__player-panel">
        {epgPanelOpen && (
          <div className="livetvcat__epg-panel">
            {selectedCategoryNode?.id !== 'schedule' && (
            <div className="livetvcat__epg-days">
              {availableDays.map((item, index) => {
                const selected = index === selectedDayIndex;
                const focused = focusArea === 'epg-days' && selected;
                return (
                  <div
                    key={`${item.label}-${item.weekday}`}
                    className={`livetvcat__epg-day${selected ? ' is-selected' : ''}${focused ? ' is-focused' : ''}`}
                    ref={(element) => {
                      epgDayRefs.current[index] = element;
                    }}
                    onClick={() => {
                      setFocusArea('epg-days');
                      setSelectedDayIndex(index);
                    }}
                  >
                    <span>{item.label}</span>
                    <span>{item.weekday}</span>
                  </div>
                );
              })}
            </div>
            )}

            <div className="livetvcat__epg-middle">
              <div className="livetvcat__epg-channel-name">{focusedChannel?.name || t('liveTV.channel')}</div>
              <div className="livetvcat__epg-list">
                {epgProgramsForDay.map((program, index) => {
                  const selected = index === selectedProgramIndex;
                  const focused = (focusArea === 'epg-list' || focusArea === 'epg-schedule') && selected;
                  const schedBtnFocused = focusArea === 'epg-schedule' && selected;
                  const scheduled = isScheduled(focusedStreamId, program.startMs);
                  const scheduleAvailable = scheduled || program.startMs > Date.now();
                  return (
                    <div
                      key={`${program.startMs}-${program.title}`}
                      className={`livetvcat__epg-item${selected ? ' is-selected' : ''}${focused ? ' is-focused' : ''}`}
                      ref={(element) => {
                        epgItemRefs.current[index] = element;
                      }}
                      onClick={() => {
                        setFocusArea('epg-list');
                        setSelectedProgramIndex(index);
                        toggleProgramSchedule(program);
                      }}
                    >
                      <div className="livetvcat__epg-item-row">
                        <span className="livetvcat__epg-time">
                          {formatHHMM(program.startMs)} - {formatHHMM(program.endMs)}
                        </span>
                        <button
                          type="button"
                          className={`livetvcat__epg-schedule-btn${scheduled ? ' is-scheduled' : ''}${schedBtnFocused ? ' is-kbd-focused' : ''}`}
                          title={scheduled ? t('liveTV.removeSchedule') : t('liveTV.addSchedule')}
                          disabled={!scheduleAvailable}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleProgramSchedule(program);
                          }}
                        >
                          {scheduled
                            ? <MdAlarmOn size={16} />
                            : <MdAlarm size={16} />}
                        </button>
                      </div>
                      <span className="livetvcat__epg-title">{program.title}</span>
                    </div>
                  );
                })}

                {!epgProgramsForDay.length && (
                  <div className="livetvcat__epg-empty">{t('liveTV.noProgramsForDate')}</div>
                )}
              </div>
            </div>

            <div className="livetvcat__epg-right">
              <div className="livetvcat__epg-right-top">
                <span className="livetvcat__epg-now">{nowClock}</span>
                <span className="livetvcat__epg-speed">{connectionMbps}</span>
              </div>

              <div className="livetvcat__epg-description-card">
                <div className="livetvcat__epg-description-title">
                  {selectedProgram?.title || currentProgram?.title || t('liveTV.noProgram')}
                </div>
                <div className="livetvcat__epg-description-time">
                  {selectedProgram
                    ? `${formatHHMM(selectedProgram.startMs)} - ${formatHHMM(selectedProgram.endMs)}`
                    : '--:--'}
                </div>
                <div className="livetvcat__epg-description">
                  {selectedProgram?.description || t('liveTV.noDescription')}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      </div>

      {parentalPasswordMode && (
        <PasswordModal
          mode={parentalPasswordMode}
          onConfirm={handleParentalPasswordConfirm}
          onCancel={() => {
            if (pendingParentalAction?.type === 'playChannel' && focusedStreamId) {
              setPlayingStreamId(focusedStreamId);
            }
            setPendingParentalAction(null);
            setParentalPasswordMode(null);
            setParentalPasswordError('');
          }}
          error={parentalPasswordError}
        />
      )}

      {showCategorySaveModal && (
        <ConfirmModal
          title={t('liveTV.saveBlockedCategoriesTitle')}
          description={t('liveTV.saveBlockedCategoriesDescription')}
          confirmLabel={t('common.save')}
          cancelLabel={t('common.cancel')}
          onConfirm={saveBlockedCategories}
          onCancel={cancelBlockedCategories}
        />
      )}
    </div>
  );
};
